import { useState, type FormEvent } from 'react';
import type { PremiumModule, CreatePremiumModuleDto } from '@yawi/types';
import { Button, Input, Textarea, Modal } from '@/core/ui/components';
import { useServices, useToast } from '@/app/providers';

interface ModuleFormModalProps {
  module?: PremiumModule;
  onClose: () => void;
  onSuccess: () => void;
}

export function ModuleFormModal({ module, onClose, onSuccess }: ModuleFormModalProps) {
  const { billingRepository } = useServices();
  const { showToast } = useToast();

  const isEdit = !!module;

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<CreatePremiumModuleDto>({
    name: module?.name || '',
    code: module?.code || '',
    description: module?.description || '',
    monthlyPrice: module?.monthlyPrice || 0,
    yearlyPrice: module?.yearlyPrice,
    sortOrder: module?.sortOrder ?? 0,
    icon: module?.icon || '',
    isActive: module?.isActive ?? true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.code.trim()) {
      newErrors.code = 'Code is required';
    } else if (!/^[a-z0-9_-]+$/.test(formData.code)) {
      newErrors.code = 'Code can only contain lowercase letters, numbers, hyphens, and underscores';
    }

    if (formData.monthlyPrice < 0) {
      newErrors.monthlyPrice = 'Monthly price cannot be negative';
    }

    if (formData.yearlyPrice !== undefined && formData.yearlyPrice < 0) {
      newErrors.yearlyPrice = 'Yearly price cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const dto: CreatePremiumModuleDto = {
        ...formData,
        yearlyPrice: formData.yearlyPrice || undefined,
      };

      if (isEdit) {
        await billingRepository.updateModule(module.id, dto);
        showToast('success', 'Module updated successfully');
      } else {
        await billingRepository.createModule(dto);
        showToast('success', 'Module created successfully');
      }
      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : `Failed to ${isEdit ? 'update' : 'create'} module`;
      showToast('error', message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Edit Module' : 'Create Module'}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={isLoading}>
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label="Name"
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          error={errors.name}
          placeholder="Advanced Analytics"
        />

        <Input
          label="Code"
          value={formData.code}
          onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value.toLowerCase() }))}
          error={errors.code}
          placeholder="advanced-analytics"
          disabled={isEdit}
        />
        {!isEdit && (
          <p className="text-xs text-theme-tertiary -mt-4">
            Unique identifier used in API calls
          </p>
        )}

        <Textarea
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
          placeholder="Advanced reporting and analytics features..."
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Monthly Price"
            type="number"
            min={0}
            step={0.01}
            value={formData.monthlyPrice}
            onChange={(e) => setFormData((prev) => ({ ...prev, monthlyPrice: parseFloat(e.target.value) || 0 }))}
            error={errors.monthlyPrice}
          />

          <Input
            label="Yearly Price (optional)"
            type="number"
            min={0}
            step={0.01}
            value={formData.yearlyPrice ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              setFormData((prev) => ({
                ...prev,
                yearlyPrice: val === '' ? undefined : (parseFloat(val) || 0),
              }));
            }}
            error={errors.yearlyPrice}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Icon"
            value={formData.icon || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, icon: e.target.value }))}
            placeholder="chart-bar"
          />

          <Input
            label="Sort Order"
            type="number"
            min={0}
            value={formData.sortOrder ?? 0}
            onChange={(e) => setFormData((prev) => ({ ...prev, sortOrder: parseInt(e.target.value) || 0 }))}
          />
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="isActive"
            checked={formData.isActive}
            onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
            className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
          />
          <label htmlFor="isActive" className="text-sm text-theme">
            Module is active and available for purchase
          </label>
        </div>
      </form>
    </Modal>
  );
}
